/**
 * Loyalty program utilities for extracting loyalty data from Adyen terminal responses
 *
 * This module provides utilities for extracting loyalty account information from Adyen Terminal API
 * responses, including both payment responses and card acquisition responses. The functions filter by the
 * configured LOYALTY_BRAND to find the relevant loyalty program.
 *
 * @module loyaltyUtils
 * @example
 * 	```typescript
 * 	import { extractLoyaltyProgram, extractLoyaltyId } from '@/src/core/utils/loyaltyUtils'
 *
 * 	// Extract loyalty account from terminal response
 * 	const loyaltyAccount = extractLoyaltyProgram(terminalResponse)
 *
 * 	if (loyaltyAccount) {
 * 	const loyaltyId = extractLoyaltyId(loyaltyAccount)
 * 	console.log(`Found loyalty ID: ${loyaltyId}`)
 * 	}
 * 	```
 */

import type { IdentificationData } from '@/src/core/types/identification.types'
import { LoyaltyAccount } from '@adyen/api-library/lib/src/typings/terminal/models'
import { TerminalApiResponse } from '@adyen/api-library/lib/src/typings/terminal/terminalApiResponse'

/**
 * The loyalty brand to filter for when extracting loyalty accounts
 *
 * Set via the LOYALTY_BRAND environment variable. Only loyalty accounts matching this brand will be returned
 * by the extraction functions.
 */
export const LOYALTY_BRAND = process.env.LOYALTY_BRAND || ''

/**
 * Extracts the loyalty account from a card acquisition response
 *
 * Searches the CardAcquisitionResponse for a loyalty account matching the configured LOYALTY_BRAND.
 *
 * @param response - The terminal API response containing card acquisition data
 * @returns The matching loyalty account, or undefined if not found
 */
function getLoyaltyAccountFromAcquisitionResponse(response: TerminalApiResponse): LoyaltyAccount | undefined {
	return response.SaleToPOIResponse?.CardAcquisitionResponse?.LoyaltyAccount?.find(
		(loyaltyAccount) => loyaltyAccount.LoyaltyBrand === LOYALTY_BRAND
	)
}

/**
 * Extracts the loyalty account from a payment response
 *
 * Searches the PaymentResponse (or RepeatedMessageResponse for transaction status queries) for a loyalty
 * account matching the configured LOYALTY_BRAND.
 *
 * @param response - The terminal API response containing payment data
 * @returns The matching loyalty account, or undefined if not found
 */
function getLoyaltyAccountFromPaymentResponse(response: TerminalApiResponse): LoyaltyAccount | undefined {
	// Extract the loyalty result from the payment response or the repeated response message body
	const loyaltyResult =
		response.SaleToPOIResponse?.PaymentResponse?.LoyaltyResult ??
		response.SaleToPOIResponse?.TransactionStatusResponse?.RepeatedMessageResponse
			?.RepeatedResponseMessageBody.PaymentResponse?.LoyaltyResult

	// Find and return the loyalty account that matches the loyalty brand
	return loyaltyResult?.find((loyalty) => loyalty.LoyaltyAccount.LoyaltyBrand === LOYALTY_BRAND)
		?.LoyaltyAccount
}

/**
 * Extracts the loyalty account from any terminal API response
 *
 * Attempts to find a loyalty account by first checking the payment response, then falling back to the card
 * acquisition response.
 *
 * @param response - The terminal API response (payment or card acquisition)
 * @returns The matching loyalty account, or undefined if not found in either response type
 */
function getLoyaltyAccountFromResponse(response: TerminalApiResponse): LoyaltyAccount | undefined {
	return getLoyaltyAccountFromPaymentResponse(response) || getLoyaltyAccountFromAcquisitionResponse(response)
}

/**
 * Extracts the loyalty program from a terminal API response
 *
 * This is the main function for extracting loyalty information from Adyen terminal responses. It searches
 * both payment and card acquisition responses for a loyalty account matching the configured LOYALTY_BRAND.
 *
 * @example
 * 	;```typescript
 * 	const response = await adyenService.acquireCard(request)
 * 	const loyaltyAccount = extractLoyaltyProgram(response)
 *
 * 	if (loyaltyAccount) {
 * 		const customerId = loyaltyAccount.LoyaltyAccountID?.LoyaltyID
 * 		// Lookup customer in backend system
 * 	}
 * 	```
 *
 * @param response - The terminal API response to extract loyalty data from
 * @returns The matching loyalty account, or undefined if no valid response or no matching loyalty found
 */
export function extractLoyaltyProgram(response?: TerminalApiResponse): LoyaltyAccount | undefined {
	if (!response?.SaleToPOIResponse) {
		console.error('Invalid response object')
		return undefined
	}

	const loyaltyAccount = getLoyaltyAccountFromResponse(response)

	if (!loyaltyAccount) {
		console.log('No loyalty program found in the payment or acquisition responses')
	}

	return loyaltyAccount
}

/**
 * Extracts the loyalty ID from a loyalty account
 *
 * Retrieves the LoyaltyID from the nested LoyaltyAccountID structure in an Adyen loyalty account object.
 *
 * @example
 * 	;```typescript
 * 	const loyaltyAccount = extractLoyaltyProgram(terminalResponse)
 * 	const loyaltyId = extractLoyaltyId(loyaltyAccount)
 *
 * 	if (loyaltyId) {
 * 		const customer = await customerService.lookupCustomer(loyaltyId)
 * 	}
 * 	```
 *
 * @param account - The loyalty account object from an Adyen terminal response
 * @returns The loyalty ID string, or undefined if not present
 */
export function extractLoyaltyId(account?: LoyaltyAccount): string | undefined {
	return account?.LoyaltyAccountID?.LoyaltyID
}

/**
 * Extracts complete identification data from a loyalty account
 *
 * Retrieves the full identification data including loyalty ID, entry mode, and identification type from a
 * loyalty account for display in the UI.
 *
 * @example
 * 	;```typescript
 * 	const loyaltyAccount = extractLoyaltyProgram(terminalResponse)
 * 	const identificationData = extractIdentificationData(loyaltyAccount)
 *
 * 	if (identificationData) {
 * 	console.log(`Entry mode: ${identificationData.entryMode}`)
 * 	}
 * 	```
 *
 * @param account - The loyalty account object from an Adyen terminal response
 * @returns The identification data object, or undefined if account or required fields are missing
 */
export function extractIdentificationData(account?: LoyaltyAccount): IdentificationData | undefined {
	if (!account?.LoyaltyAccountID) {
		return undefined
	}

	const { LoyaltyAccountID: accountId, LoyaltyBrand } = account
	const loyaltyId = accountId.LoyaltyID

	if (!loyaltyId || !accountId.EntryMode || !accountId.IdentificationType) {
		return undefined
	}

	return {
		loyaltyId,
		entryMode: accountId.EntryMode,
		identificationType: accountId.IdentificationType,
		loyaltyBrand: LoyaltyBrand,
	}
}
